import React from 'react';
import { Box, MenuItem, IconButton, Menu } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { activateLocale, getCurrentLocale, i18n, locales } from '../i18n';
import LanguageIcon from '@mui/icons-material/Language';

const LanguageSwitcher: React.FC = () => {
  const currentLocale = getCurrentLocale();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (newLocale: string) => {
    const oldLocale = currentLocale;

    // Close the menu
    handleMenuClose();

    // If same language selected, do nothing
    if (newLocale === oldLocale) return;

    // Activate the new locale first
    activateLocale(newLocale);

    // Get the current path and query parameters
    const currentPath = location.pathname;
    const queryParams = location.search;

    // Check if we're switching from a localized route
    const isFromLocalizedRoute = currentPath.startsWith(`/${oldLocale}/`) || currentPath === `/${oldLocale}`;

    // Determine the new URL based on the current path and new locale
    let newUrl: string;

    // If we're at the root of a localized route (e.g., /pt-BR)
    if (currentPath === `/${oldLocale}`) {
      // Navigate to the root for English or the localized root for other languages
      // Preserve query parameters
      const newPath = newLocale === 'en' ? '/' : `/${newLocale}`;
      newUrl = `${newPath}${queryParams}`;
    }
    // If we're on a localized path (e.g., /pt-BR/analytics)
    else if (isFromLocalizedRoute) {
      // Extract the path without the locale prefix
      const pathWithoutLocale = currentPath.replace(`/${oldLocale}`, '');

      // For English, remove the locale prefix; for others, replace it
      const newPath = newLocale === 'en'
        ? (pathWithoutLocale || '/') // Handle empty path edge case
        : `/${newLocale}${pathWithoutLocale}`;

      // Preserve query parameters
      newUrl = `${newPath}${queryParams}`;
    }
    // If we're on a non-localized path (e.g., /analytics) and switching to a non-English locale
    else if (newLocale !== 'en') {
      // Preserve query parameters
      newUrl = `/${newLocale}${currentPath}${queryParams}`;
    }
    // If we're on a non-localized path and switching to English, stay on the same path
    else {
      newUrl = `${currentPath}${queryParams}`;
    }

    // Use window.location.href to force a full page reload with the new URL
    window.location.href = newUrl;
  };

  return (
    <Box>
      <IconButton
        size="small"
        onClick={handleMenuOpen}
        aria-label={i18n._("Change language")}
        sx={{
          color: 'primary.main',
          '&:hover': {
            backgroundColor: 'rgba(0, 204, 204, 0.1)',
          }
        }}
      >
        <LanguageIcon fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            border: '1px solid',
            borderColor: 'primary.main',
            minWidth: '120px',
          }
        }}
      >
        {Object.entries(locales).map(([locale, name]) => (
          <MenuItem
            key={locale}
            onClick={() => handleLanguageChange(locale)}
            selected={locale === currentLocale}
            sx={{
              color: '#fff',
              '&.Mui-selected': {
                backgroundColor: 'rgba(0, 204, 204, 0.2)',
              },
              '&:hover': {
                backgroundColor: 'rgba(0, 204, 204, 0.1)',
              }
            }}
          >
            {name}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default LanguageSwitcher;
